<!-- src/views/reviews/ClientFeedbackFormView.vue -->
<template>
  <div class="min-h-screen bg-[#F0F0F0] font-sans text-left">
    <!-- Dynamic Public Header Banner -->
    <div class="bg-[#0A1630] py-8 px-6 sm:px-10 border-b-4 border-[#20FCF1] shadow-xl text-center">
      <div class="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
        <div class="flex items-center gap-4 text-left">
          <div class="bg-white p-2.5 rounded-2xl shadow-md">
            <!-- 🛡️ MULTI-TENANT DYNAMIC LOGO FETCH -->
            <img v-if="clientLogoUrl" :src="clientLogoUrl" class="h-10 max-w-[150px] object-contain" alt="Client Logo" />
            <img v-else src="/images/sentinel-safe-logo.png" class="h-10 w-auto object-contain" alt="Sentinel Safe Logo" />
          </div>
          <div>
            <h1 class="text-xl sm:text-2xl font-black text-white uppercase tracking-tight">
              {{ clientDisplayName ? `${clientDisplayName}: ` : '' }}Client Feedback Questionnaire
            </h1>
            <p class="text-[#20FCF1] font-black uppercase text-[9px] tracking-[0.2em] mt-1">Sentinel Safe Quality Assurance Protocol</p>
          </div>
        </div>
        <div class="bg-white/5 px-4 py-2 rounded-xl border border-white/10 text-right">
          <span class="text-[9px] font-black text-slate-300 uppercase tracking-widest block">Standard Audit Protocol</span>
        </div>
      </div>
    </div>

    <!-- MASTER CONTAINER (Synchronised with Dashboard Alignment - max-w-7xl) -->
    <div class="p-4 sm:p-6 md:p-8">
      <div class="max-w-7xl mx-auto">
        <div class="bg-white p-6 sm:p-12 rounded-[40px] shadow-xl border border-slate-200">
          
          <!-- Success State -->
          <div v-if="successMessage" class="py-20 text-center space-y-6 animate-fadeIn">
            <div class="w-20 h-20 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto shadow-inner">
              <CheckCircleIcon class="h-10 w-10" />
            </div>
            <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">Thank You For Your Feedback!</h2>
            <p class="text-sm font-medium text-slate-600 max-w-md mx-auto leading-relaxed">
              Your questionnaire response has been securely logged into the Sentinel Safe compliance register. We appreciate your insights.
            </p>
          </div>

          <!-- Questionnaire Form -->
          <form v-else @submit.prevent="handleSubmit" class="space-y-12 text-left">
            
            <!-- 1. PROJECT DETAILS -->
            <section class="space-y-6">
              <div class="border-b border-slate-200 pb-3">
                <h3 class="text-xs font-black text-[#0A1630] uppercase tracking-[0.2em]">1. Project Details</h3>
              </div>

              <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label class="form-label">Client / Project Name <span class="text-red-500">*</span></label>
                  <input type="text" v-model="formData.projectName" required class="form-input" placeholder="e.g. Network Rail / Client Name" />
                </div>
                <div>
                  <label class="form-label">Project Ref (Job No.) <span class="text-red-500">*</span></label>
                  <input type="text" v-model="formData.projectRef" required class="form-input" placeholder="e.g. J-1001" />
                </div>
                <div>
                  <label class="form-label">Date of Feedback <span class="text-red-500">*</span></label>
                  <input type="date" v-model="formData.feedbackDate" required class="form-input" />
                </div>
                <div>
                  <label class="form-label">Your Name <span class="text-red-500">*</span></label>
                  <input type="text" v-model="formData.respondentName" required class="form-input" placeholder="Site Manager Name" />
                </div>
                <div>
                  <label class="form-label">Position / Role <span class="text-red-500">*</span></label>
                  <input type="text" v-model="formData.respondentRole" required class="form-input" placeholder="e.g. Construction Manager" />
                </div>
                <div>
                  <label class="form-label">Department / Source</label>
                  <input type="text" v-model="formData.departmentSource" class="form-input" placeholder="e.g. Safety / Operations" />
                </div>
              </div>
            </section>

            <!-- 2. FEEDBACK TYPE -->
            <section class="space-y-4">
              <div class="border-b border-slate-200 pb-3">
                <h3 class="text-xs font-black text-[#0A1630] uppercase tracking-[0.2em]">2. Feedback Type</h3>
                <p class="text-[10px] text-slate-400 font-bold uppercase mt-1">Tick the option that best describes this return. Use more than one box only if needed.</p>
              </div>

              <div class="grid grid-cols-2 sm:grid-cols-5 gap-3">
                <label v-for="typeOpt in feedbackTypeOptions" :key="typeOpt" class="flex items-center gap-2.5 p-4 bg-slate-50 rounded-2xl border border-slate-200 cursor-pointer hover:bg-slate-100 transition-all">
                  <input type="checkbox" v-model="formData.feedbackTypes" :value="typeOpt" class="w-4 h-4 text-[#0A1630] rounded border-slate-300" />
                  <span class="text-[10px] font-black uppercase text-[#0A1630] tracking-tight">{{ typeOpt }}</span>
                </label>
              </div>
            </section>

            <!-- 3. SERVICE RATINGS -->
            <section class="space-y-6">
              <div class="border-b border-slate-200 pb-3">
                <h3 class="text-xs font-black text-[#0A1630] uppercase tracking-[0.2em]">3. Service Ratings</h3>
                <p class="text-[10px] text-slate-400 font-bold uppercase mt-1">Please tick one box per row (5 = Highest / Best, 1 = Lowest / Worst).</p>
              </div>

              <div class="space-y-6">
                <div v-for="(question, qIdx) in serviceQuestions" :key="qIdx" class="p-6 bg-slate-50 rounded-3xl border border-slate-200 space-y-4">
                  <p class="text-xs font-black text-[#0A1630] uppercase leading-relaxed">{{ qIdx + 1 }}. {{ question }}</p>
                  
                  <div class="grid grid-cols-5 gap-2 text-center">
                    <label v-for="score in [5, 4, 3, 2, 1]" :key="score" 
                      :class="formData.serviceRatings[qIdx] === score ? 'bg-[#0A1630] text-[#20FCF1] border-[#0A1630]' : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-100'"
                      class="p-3 rounded-2xl border flex flex-col items-center justify-center gap-1 cursor-pointer transition-all shadow-sm"
                    >
                      <input type="radio" :name="'question_' + qIdx" :value="score" v-model.number="formData.serviceRatings[qIdx]" required class="sr-only" />
                      <span class="text-sm font-black">{{ score }}</span>
                      <span class="text-[7px] font-bold uppercase tracking-tight opacity-75">
                        {{ score === 5 ? 'Excellent' : score === 4 ? 'Good' : score === 3 ? 'Average' : score === 2 ? 'Below' : 'Poor' }}
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </section>

            <!-- 4. OVERALL SATISFACTION -->
            <section class="space-y-6">
              <div class="border-b border-slate-200 pb-3">
                <h3 class="text-xs font-black text-[#0A1630] uppercase tracking-[0.2em]">4. Overall Satisfaction</h3>
                <p class="text-[10px] text-slate-400 font-bold uppercase mt-1">Overall, how satisfied are you with our service on this project? Tick one (1 to 5).</p>
              </div>

              <div class="grid grid-cols-5 gap-2 text-center">
                <label v-for="score in [1, 2, 3, 4, 5]" :key="'overall_' + score" 
                  :class="formData.overallSatisfaction === score ? 'bg-[#0A1630] text-[#20FCF1] border-[#0A1630]' : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'"
                  class="p-4 rounded-2xl border flex flex-col items-center justify-center gap-1 cursor-pointer transition-all shadow-sm"
                >
                  <input type="radio" name="overallSatisfaction" :value="score" v-model.number="formData.overallSatisfaction" required class="sr-only" />
                  <span class="text-base font-black">{{ score }}</span>
                  <span class="text-[8px] font-bold uppercase tracking-tight opacity-75">
                    {{ score === 1 ? 'Unacceptable' : score === 2 ? 'Below' : score === 3 ? 'Average' : score === 4 ? 'Good' : 'Excellent' }}
                  </span>
                </label>
              </div>

              <div class="pt-4">
                <label class="form-label">How likely are you to recommend us to a colleague or another organisation? (0 = Not at all likely → 5 = Extremely likely)</label>
                <div class="grid grid-cols-6 gap-2 text-center mt-2">
                  <label v-for="rec in [0, 1, 2, 3, 4, 5]" :key="'rec_' + rec" 
                    :class="formData.recommendationLikelihood === rec ? 'bg-[#0A1630] text-[#20FCF1] border-[#0A1630]' : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'"
                    class="p-3 rounded-2xl border flex flex-col items-center justify-center cursor-pointer transition-all shadow-sm"
                  >
                    <input type="radio" name="recommendationLikelihood" :value="rec" v-model.number="formData.recommendationLikelihood" required class="sr-only" />
                    <span class="text-sm font-black">{{ rec }}</span>
                  </label>
                </div>
              </div>
            </section>

            <!-- 5. COMMENTS & SUGGESTIONS -->
            <section class="space-y-4">
              <div class="border-b border-slate-200 pb-3">
                <h3 class="text-xs font-black text-[#0A1630] uppercase tracking-[0.2em]">5. Comments & Suggestions</h3>
                <p class="text-[10px] text-slate-400 font-bold uppercase mt-1">Please share any additional feedback, what we did well, or areas where we could improve:</p>
              </div>
              <textarea v-model="formData.comments" rows="5" class="form-input bg-slate-50" placeholder="Enter comments or suggestions..."></textarea>
            </section>

            <!-- 6. COMPLETION & SIGNATURE -->
            <section class="space-y-6">
              <div class="border-b border-slate-200 pb-3">
                <h3 class="text-xs font-black text-[#0A1630] uppercase tracking-[0.2em]">6. Completion & Sign-Off</h3>
              </div>

              <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label class="form-label">Date <span class="text-red-500">*</span></label>
                  <input type="date" v-model="formData.completionDate" required class="form-input" />
                </div>
              </div>

              <div class="bg-slate-50 p-8 rounded-[32px] border border-slate-200 flex flex-col items-center">
                <div class="w-full max-w-md bg-white p-6 rounded-[28px] shadow-inner border border-slate-200">
                  <label class="block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 text-center mb-4">Client Signature <span class="text-red-500">*</span></label>
                  <Vue3Signature 
                    ref="signaturePad" 
                    :sigOption="signatureOptions" 
                    :h="'180px'" 
                    :w="'100%'" 
                    class="border border-slate-100 rounded-2xl bg-slate-50"
                  ></Vue3Signature>
                  <div class="mt-4 flex gap-3">
                    <button type="button" @click="saveSignaturePad" class="flex-1 py-3 bg-[#0A1630] text-white rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-slate-800 transition-all">Confirm Signature</button>
                    <button type="button" @click="clearSignaturePad" class="px-6 py-3 bg-slate-100 text-slate-500 rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-slate-200 transition-all">Clear</button>
                  </div>
                </div>
              </div>
            </section>

            <!-- Submit Action -->
            <div class="pt-6">
              <button type="submit" :disabled="isSubmitting || !isFormValid" class="btn-premium">
                <span v-if="isSubmitting" class="flex items-center justify-center gap-3">
                    <svg class="animate-spin h-6 w-6" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a88 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                    Submitting Questionnaire...
                </span>
                <span v-else>Submit Client Questionnaire</span>
              </button>
              <p v-if="submissionError" class="mt-4 text-center text-rose-600 font-bold uppercase text-xs tracking-widest">{{ submissionError }}</p>
            </div>

          </form>
        </div>
      </div>
    </div>

    <!-- 🛡️ MASTER CORPORATE FOOTER (Matching all other master layouts) -->
    <footer class="bg-white border-t border-slate-200 py-12 mt-20 no-print">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 text-center space-y-4">
        <div class="flex items-center justify-center gap-3">
          <img src="/images/sentinel-safe-logo.png" class="h-6 w-auto object-contain" alt="Sentinel Safe Logo" />
          <span class="font-sans font-black text-xs uppercase tracking-[0.2em] text-[#0A1630]">Sentinel Safe</span>
        </div>
        <p class="text-[9px] font-bold text-slate-400 uppercase tracking-widest leading-relaxed">
          Copyright &copy; 2026 Sentinel Safe. All Rights Reserved.
        </p>
        <p class="text-[8px] font-bold text-slate-400 uppercase tracking-wider">
          Sentinel Safe is operated and managed by Get Pixel Perfection Limited.<br>
          Get Pixel Perfection Limited is a company registered in England and Wales. Company Number: 16854093.<br>
          UK GDPR Compliant • Privacy Policy
        </p>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { db, firebaseApp } from '@/firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import Vue3Signature from 'vue3-signature';
import { CheckCircleIcon } from '@heroicons/vue/24/outline';

const route = useRoute();
const functions = getFunctions(firebaseApp, 'europe-west2');

const isSubmitting = ref(false);
const submissionError = ref('');
const successMessage = ref(false);
const clientLogoUrl = ref('');
const clientDisplayName = ref('');

const signaturePad = ref(null);
const signatureOptions = { penColor: "#000", backgroundColor: "rgba(255,255,255,0)" };

const feedbackTypeOptions = ['Performance rating', 'Praise', 'Concern', 'Suggestion for improvement', 'General comment'];

const serviceQuestions = [
  "How well did the labour team understand the scope of works, method statements, and site-specific safety requirements before starting?",
  "How would you rate the skill, experience, and overall competence of the operatives supplied to this project?",
  "How would you rate the quality, accuracy, and finish of the work completed on site?",
  "How well did the team plan, manage, and deliver the works against the agreed programme and key dates?",
  "How easy was it to communicate with the team on site (including spoken English and understanding of instructions)?",
  "How well did the team work safely on site, follow RAMS/PPE rules, and keep the work area tidy and controlled?",
  "How reliably did the team follow instructions, complete allocated tasks, and cooperate with your staff and other contractors?",
  "How satisfied are you with the supervisor/team leader on site, including their competence, attitude, organisation, and ability to keep the job moving?"
];

const formData = reactive({
  projectName: '',
  projectRef: '',
  feedbackDate: new Date().toISOString().substr(0, 10),
  respondentName: '',
  respondentRole: '',
  departmentSource: '',
  feedbackTypes: [],
  serviceRatings: {},
  overallSatisfaction: null,
  recommendationLikelihood: null,
  comments: '',
  completionDate: new Date().toISOString().substr(0, 10),
  signatureDataUrl: ''
});

const isFormValid = computed(() => {
  return formData.projectName && 
         formData.projectRef && 
         formData.respondentName && 
         formData.respondentRole && 
         formData.overallSatisfaction !== null && 
         formData.recommendationLikelihood !== null && 
         formData.signatureDataUrl && 
         !isSubmitting.value;
});

const saveSignaturePad = () => { 
  if (signaturePad.value && !signaturePad.value.isEmpty()) {
    formData.signatureDataUrl = signaturePad.value.save('image/png');
  } 
};

const clearSignaturePad = () => { 
  if (signaturePad.value) { 
    signaturePad.value.clear(); 
    formData.signatureDataUrl = ''; 
  } 
};

// Dynamically resolve client branding from the target user profile [1]
const resolveClientBranding = async () => {
  try {
    const targetUserId = route.params.userId || route.params.id;
    if (!targetUserId) return;

    const uSnap = await getDoc(doc(db, 'userProfiles', targetUserId));
    if (uSnap.exists()) {
      const clientId = uSnap.data().clientId;
      if (clientId) {
        const cSnap = await getDoc(doc(db, 'clients', clientId));
        if (cSnap.exists()) {
          const cData = cSnap.data();
          clientLogoUrl.value = cData.logoUrl || cData.clientLogoUrl || cData.logo || '';
          clientDisplayName.value = cData.companyDisplayName || cData.clientName || '';
        }
      }
    }
  } catch (err) {
    console.warn("Dynamic client branding resolution issue:", err);
  }
};

onMounted(() => {
  resolveClientBranding();
});

const handleSubmit = async () => {
  submissionError.value = '';
  if (!formData.signatureDataUrl) {
    submissionError.value = "Client signature seal is required.";
    return;
  }

  isSubmitting.value = true;
  try {
    const targetUserId = route.params.userId || route.params.id || 'system';
    
    let clientId = 'default';
    const uSnap = await getDoc(doc(db, 'userProfiles', targetUserId));
    if (uSnap.exists()) {
      clientId = uSnap.data().clientId || 'default';
    }

    const result = await httpsCallable(functions, 'submitGenericForm')({
      formIdentifier: 'clientFeedbackForm',
      targetCollection: 'healthsafetyFormSubmissions',
      submissionData: {
        ...formData,
        userId: targetUserId,
        clientUid: clientId,
        clientId: clientId,
        isPublicSubmission: true,
        submittedAt: new Date().toISOString()
      }
    });

    if (!result.data.success) throw new Error("Submission dispatch failed.");
    successMessage.value = true;
  } catch (err) {
    submissionError.value = "Submission failure: " + err.message;
    isSubmitting.value = false;
  }
};
</script>

<style scoped>
.form-input { @apply block w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-700 shadow-sm transition-all focus:ring-2 focus:ring-[#20FCF1] focus:border-[#20FCF1] sm:text-sm font-bold; }
.form-label { @apply block text-[11px] font-black text-[#0A1630] uppercase tracking-[0.2em] mb-2; }
.btn-premium { @apply w-full bg-[#20FCF1] text-[#0A1630] py-6 rounded-[32px] text-xl font-black uppercase tracking-[0.2em] shadow-2xl hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-50 disabled:grayscale flex justify-center items-center cursor-pointer; }
.animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
</style>